import { TaxonomyResponseDto } from "../components/common/ObjectTypes";
import axios from "./axios";

// Interfaces for API responses based on the backend DTOs
interface TaxonomyListResponse {
  items: TaxonomyResponseDto[];
  total_count: number;
}

interface TaxonomySearchResponse {
  items: TaxonomyResponseDto[];
}

/**
 * Fetches taxonomy data from the backend API
 * @param skip Number of records to skip for pagination
 * @param take Maximum number of records to return
 * @returns Promise with the taxonomy data and total count
 */
export const fetchTaxonomyData = async (
  skip: number = 0,
  take: number = 1000,
): Promise<{ items: TaxonomyResponseDto[]; total_count: number }> => {
  try {
    const response = await axios.get<TaxonomyListResponse>("/taxonomy", {
      params: { skip, take },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching taxonomy data:", error);
    throw error;
  }
};

/**
 * Search for taxonomy items by specific criteria
 * @param searchTerm The term to search for
 * @param take Maximum number of results to return
 * @returns Promise with the filtered taxonomy data
 */
export const searchTaxonomy = async (
  searchTerm: string,
  take: number = 50,
): Promise<TaxonomyResponseDto[]> => {
  try {
    const term = searchTerm?.trim() ?? "";
    if (term.length < 3) {
      return [];
    }

    const response = await axios.get<TaxonomySearchResponse>(
      "/taxonomy/search",
      {
        params: { search: term, take },
      },
    );

    return response.data.items;
  } catch (error) {
    console.error("Error searching taxonomy data:", error);
    // Return empty array instead of crashing to match backend behavior
    return [];
  }
};

/**
 * Get taxonomy details by unique identifier
 * @param uniqueIdentifier The unique identifier of the taxonomy item
 * @returns Promise with the taxonomy item details
 */
export const getTaxonomyById = async (
  uniqueIdentifier: string,
): Promise<TaxonomyResponseDto> => {
  try {
    const response = await axios.get<TaxonomyResponseDto>(
      `/taxonomy/${uniqueIdentifier}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching taxonomy details for ${uniqueIdentifier}:`,
      error,
    );
    throw error;
  }
};
